import { type Activity, ACTIVITY_PRIORITIES, type ObjectName } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type TouchEvent, useRef, useState } from 'react';
import { api, ApiError, qs } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/format';
import { markTaskDone, reducedMotion, toast } from '../lib/motion';
import { useSession, useUsers } from '../lib/session';
import { RecordLookup } from './FieldInput';
import { DictateButton, splitVoice, uploadRecording, VoicePlayer, VoiceRecorder, withVoice } from './VoiceNote';
import { confirmDialog } from '../lib/dialogs';
import { Icon } from './Icon';

type Mode = 'email' | 'event' | 'call' | 'task' | 'voice';
interface ActivityList { activities: Activity[]; refs: Record<string, string> }

/** Where a new activity attaches, given the record page it's created from. */
function relationFor(objectName: ObjectName, recordId: string, accountId?: string | null) {
  if (objectName === 'lead') return { whoType: 'lead', whoId: recordId };
  if (objectName === 'contact') return { whoType: 'contact', whoId: recordId, ...(accountId ? { whatType: 'account', whatId: accountId } : {}) };
  return { whatType: objectName, whatId: recordId };
}

const MODE_LABEL: Record<Mode, string> = { email: 'Log an email', event: 'New event', call: 'Log a call', task: 'New task', voice: 'Voice note' };

/** Round coloured icons for each kind of activity (email grey, event purple, call teal, task green). */
const ICON_PATH: Record<Mode, string> = {
  email: 'M4 6.5h12v8H4zM4.5 7l5.5 4.2L15.5 7',
  event: 'M4.5 6h11v10h-11zM4.5 9h11M7.5 4.5v3M12.5 4.5v3M7 11.5h1.5M11 11.5h1.5M7 13.8h1.5',
  call: 'M6.2 4.5 8 4l1.2 3-1.4 1c.6 1.5 1.7 2.6 3.2 3.2l1-1.4 3 1.2-.5 1.8c-.2.6-.8 1-1.4.9C8.7 13.3 6.7 11.3 5.3 5.9c-.1-.6.3-1.2.9-1.4Z',
  task: 'M5 6.5l1.2 1.2L8.5 5.4M10.5 6.5H15M5 11l1.2 1.2L8.5 9.9M10.5 11H15M6 14.5h.01M10.5 14.5H15',
  voice: 'M10 3.8a2 2 0 0 1 2 2v4a2 2 0 0 1-4 0v-4a2 2 0 0 1 2-2ZM6.3 9.6a3.7 3.7 0 0 0 7.4 0M10 13.3v2.6M7.6 15.9h4.8',
};
export function ActIcon({ kind, size = 'm' }: { kind: Mode; size?: 's' | 'm' }) {
  return (
    <span className={`act-icon ${kind} ${size}`} aria-hidden="true">
      <svg viewBox="0 0 20 20"><path d={ICON_PATH[kind]} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </span>
  );
}
const kindOf = (a: Activity): Mode => (a.kind === 'event' ? 'event' : a.subtype === 'call' ? 'call' : a.subtype === 'email' ? 'email' : splitVoice(a.description).fileId || /^voice note/i.test(a.subject) ? 'voice' : 'task');

// The local day (Saudi time), not UTC: between midnight and 3 AM the UTC date is still yesterday.
const localYmd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = () => localYmd(new Date());
const isOpenTask = (a: Activity) => a.kind === 'task' && a.status !== 'completed' && a.status !== 'deferred';
const isUpcomingEvent = (a: Activity) => a.kind === 'event' && !!a.endAt && new Date(a.endAt) >= new Date();

const addDays = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return localYmd(d); };
const NEXT_IN: { label: string; days: number }[] = [{ label: '1 week', days: 7 }, { label: '2 weeks', days: 14 }, { label: '1 month', days: 30 }, { label: '6 weeks', days: 42 }];

/**
 * After a meeting or call on a deal with no open task: "What's the next step?". Saving makes it a task, which then
 * is the deal's Next action; Skip leaves the deal showing "Set next action".
 */
function NextStepPrompt({ objectName, recordId, accountId, onClose }: { objectName: ObjectName; recordId: string; accountId?: string | null; onClose: () => void }) {
  const { user } = useSession();
  const [text, setText] = useState('Follow up with the customer');
  const [due, setDue] = useState(addDays(7));
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api('/api/activities', { method: 'POST', body: { kind: 'task', subtype: 'task', subject: text.trim().slice(0, 255), dueDate: due, ownerId: user.id, ...relationFor(objectName, recordId, accountId) } }),
    onSuccess: () => { toast(`Next step set for ${formatDate(due)}`); onClose(); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save.'),
  });
  return (
    <form className="composer next-step-ask" onSubmit={(e) => { e.preventDefault(); if (text.trim() && due) save.mutate(); }}>
      <h4 className="composer-title">What’s the next step?</h4>
      <label className="field"><span className="sr-only">Next step</span>
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={255} required autoFocus />
      </label>
      <div className="seg" role="group" aria-label="Due">
        {NEXT_IN.map((o) => <button type="button" key={o.days} aria-pressed={due === addDays(o.days)} onClick={() => setDue(addDays(o.days))}>{o.label}</button>)}
        <input type="date" aria-label="Due date" value={due} min={today()} onChange={(e) => setDue(e.target.value)} />
      </div>
      {error && <p className="form-error">{error}</p>}
      <div className="composer-foot">
        <button type="button" className="btn pill-btn small" onClick={onClose}>Skip</button>
        <button className="btn primary pill-btn small" disabled={save.isPending || !text.trim() || !due}>{save.isPending ? 'Saving…' : 'Set next step'}</button>
      </div>
    </form>
  );
}

/** `contact`: a finished conversation was recorded (a call, email or voice note, or a meeting already over). */
function Composer({ mode, repeat, objectName, recordId, accountId, onDone, onCancel }: { mode: Mode; repeat?: boolean; objectName: ObjectName; recordId: string; accountId?: string | null; onDone: (contact: boolean) => void; onCancel: () => void }) {
  const { user, permissions } = useSession();
  const users = useUsers();
  const [notifyIds, setNotifyIds] = useState<string[]>([]);
  // Meetings: colleagues joining (e.g. a pre-sales engineer on a customer visit): it goes in their calendar.
  const [guestIds, setGuestIds] = useState<string[]>([]);
  // Who the activity is assigned to: everyone else can be invited (also yourself, to a colleague's meeting).
  const [owner, setOwner] = useState<string>(user.id);
  const [shared, setShared] = useState<{ id: string; name: string }[]>([]);
  const [picker, setPicker] = useState(0); // remounts the lookup to clear it after each pick
  const [error, setError] = useState<string | null>(null);
  const [voiceText, setVoiceText] = useState('');
  const [audio, setAudio] = useState<Blob | null>(null);
  const [uploading, setUploading] = useState(false);
  const desc = useRef<HTMLTextAreaElement>(null);
  const logged = mode === 'call' || mode === 'email' || mode === 'voice';
  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api('/api/activities', { method: 'POST', body }),
    onSuccess: (_r, body) => { setError(null); onDone(logged || (body.kind === 'event' && !!body.endAt && new Date(String(body.endAt)) <= new Date())); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save.'),
  });

  async function saveVoice(form: HTMLFormElement, subject: string) {
    if (!voiceText.trim() && !audio) { setError('Record a voice note or write the text first.'); return; }
    setUploading(true);
    // The recording goes to the record's files; the text is saved even if the upload isn't possible.
    const fileId = audio ? await uploadRecording(objectName, recordId, audio) : null;
    setUploading(false);
    const body = {
      kind: 'task', subtype: 'task', status: 'completed', dueDate: today(), subject: subject || 'Voice note',
      description: withVoice(voiceText || '(voice note without text)', fileId), ownerId: user.id,
      ...(notifyIds.length ? { notifyUserIds: notifyIds } : {}), ...relationFor(objectName, recordId, accountId),
    };
    create.mutate(body, { onSuccess: () => { form.reset(); setVoiceText(''); setAudio(null); setNotifyIds([]); } });
  }

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const f = Object.fromEntries(new FormData(form).entries()) as Record<string, string>;
    if (mode === 'voice') { void saveVoice(form, f.subject ?? ''); return; }
    const rel = relationFor(objectName, recordId, accountId);
    const common = { subject: f.subject, description: f.description || null, ownerId: f.ownerId || user.id, ...(notifyIds.length ? { notifyUserIds: notifyIds } : {}), ...(shared.length ? { relatedContactIds: shared.map((s) => s.id) } : {}), ...rel };
    let body: Record<string, unknown>;
    if (mode === 'call' || mode === 'email') body = { kind: 'task', subtype: mode, status: 'completed', dueDate: today(), ...common };
    else if (mode === 'task') body = { kind: 'task', subtype: 'task', dueDate: f.dueDate || null, priority: f.priority, ...common };
    else {
      const start = new Date(`${f.date}T${f.start}`);
      const end = new Date(`${f.date}T${f.end}`);
      if (end <= start) { setError('The end must be after the start.'); return; }
      body = { kind: 'event', subtype: 'meeting', startAt: start.toISOString(), endAt: end.toISOString(), location: f.location || null, ...(guestIds.length ? { guestUserIds: guestIds } : {}), ...common };
    }
    if (!logged && f.repeat) body.recurrence = { frequency: f.repeat, interval: 1, count: Number(f.repeatCount) || 4 };
    create.mutate(body, { onSuccess: () => { form.reset(); setNotifyIds([]); setShared([]); setGuestIds([]); } });
  }

  return (
    <form className="composer" onSubmit={submit}>
      <h4 className="composer-title"><ActIcon kind={mode} />{MODE_LABEL[mode]}</h4>
      <div className="composer-grid">
        <div className="field wide">
          <label htmlFor="act-subject">Subject</label>
          <input id="act-subject" name="subject" required maxLength={255} autoFocus
            defaultValue={mode === 'voice' ? 'Voice note' : undefined}
            placeholder={mode === 'call' ? 'Discovery call' : mode === 'email' ? 'Sent the quotation' : mode === 'task' ? 'Send proposal' : 'Site visit'} />
        </div>
        {mode === 'task' && (
          <>
            <div className="field"><label htmlFor="act-due">Due date</label><input id="act-due" name="dueDate" type="date" defaultValue={today()} /></div>
            <div className="field">
              <label htmlFor="act-pri">Priority</label>
              <select id="act-pri" name="priority" defaultValue="normal">{ACTIVITY_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}</select>
            </div>
          </>
        )}
        {mode === 'event' && (
          <>
            <div className="field"><label htmlFor="act-date">Date</label><input id="act-date" name="date" type="date" required defaultValue={today()} /></div>
            <div className="field"><label htmlFor="act-start">Start</label><input id="act-start" name="start" type="time" required defaultValue="10:00" /></div>
            <div className="field"><label htmlFor="act-end">End</label><input id="act-end" name="end" type="time" required defaultValue="10:30" /></div>
            <div className="field"><label htmlFor="act-loc">Location</label><input id="act-loc" name="location" maxLength={255} /></div>
            <div className="field wide">
              <label htmlFor="act-guests">Colleagues joining</label>
              {guestIds.length > 0 && (
                <ul className="chips">
                  {guestIds.map((g) => {
                    const n = users.data?.find((u) => u.id === g)?.name ?? 'User';
                    return <li key={g}>{n}<button type="button" className="icon small" aria-label={`Remove ${n}`} onClick={() => setGuestIds((l) => l.filter((x) => x !== g))}>×</button></li>;
                  })}
                </ul>
              )}
              <select id="act-guests" value="" onChange={(e) => { const v = e.target.value; if (v) setGuestIds((l) => [...l, v]); }}>
                <option value="">+ Add a person (gets an invitation, in their calendar)…</option>
                {users.data?.filter((u) => u.isActive && u.id !== owner && !guestIds.includes(u.id)).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </>
        )}
        {!logged && (
          <div className="field">
            <label htmlFor="act-owner">Assigned to</label>
            <select id="act-owner" name="ownerId" value={owner} onChange={(e) => { setOwner(e.target.value); setGuestIds((l) => l.filter((x) => x !== e.target.value)); }}>
              {users.data?.filter((u) => u.isActive).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        )}
        {!logged && (
          <div className="field">
            <label htmlFor="act-repeat">Repeat</label>
            <span className="row-fields">
              <select id="act-repeat" name="repeat" defaultValue={repeat ? 'weekly' : ''}>
                <option value="">Doesn’t repeat</option><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
              </select>
              <input name="repeatCount" type="number" min={2} max={52} defaultValue={4} aria-label="Number of times" title="Number of times" />
            </span>
          </div>
        )}
        {mode === 'voice' && <VoiceRecorder text={voiceText} setText={(fn) => setVoiceText(fn)} onAudio={setAudio} />}
        {mode !== 'voice' && objectName !== 'lead' && permissions.objects.contact.read && (
          <div className="field wide">
            <label htmlFor="act-shared">{objectName === 'contact' ? 'Also related to' : 'Contacts'}</label>
            {shared.length > 0 && (
              <ul className="chips">
                {shared.map((s) => (
                  <li key={s.id}>{s.name}<button type="button" className="icon small" aria-label={`Remove ${s.name}`} onClick={() => setShared((l) => l.filter((x) => x.id !== s.id))}>×</button></li>
                ))}
              </ul>
            )}
            <RecordLookup key={picker} id="act-shared" object="contact" value={null}
              onChange={(id, name) => { if (id && id !== recordId && !shared.some((s) => s.id === id) && shared.length < 50) setShared((l) => [...l, { id, name: name ?? '' }]); setPicker((n) => n + 1); }} />
          </div>
        )}
        <details className="notify field wide">
          <summary>Notify teammates{notifyIds.length ? ` (${notifyIds.length})` : ''}</summary>
          <div className="checks">
            {users.data?.filter((u) => u.isActive && u.id !== user.id).map((u) => (
              <label key={u.id} className="radio">
                <input type="checkbox" checked={notifyIds.includes(u.id)}
                  onChange={(e) => setNotifyIds((ids) => (e.target.checked ? [...ids, u.id] : ids.filter((x) => x !== u.id)))} /> {u.name}
              </label>
            ))}
          </div>
        </details>
        {mode !== 'voice' && (
          <div className="field wide">
            <span className="label-row"><label htmlFor="act-desc">{mode === 'call' ? 'Call notes' : mode === 'email' ? 'What was sent or agreed' : 'Details'}</label>
              <DictateButton onText={(p) => { const t = desc.current; if (t) t.value = t.value ? `${t.value} ${p}` : p; }} /></span>
            <textarea id="act-desc" ref={desc} name="description" rows={2} maxLength={32000} dir="auto" />
          </div>
        )}
      </div>
      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="composer-foot"><button type="button" className="btn pill-btn small" onClick={onCancel}>Cancel</button><button className="btn primary pill-btn small" disabled={create.isPending || uploading}>{uploading ? 'Saving the recording…' : mode === 'voice' ? 'Save voice note' : logged ? 'Log it' : 'Save'}</button></div>
    </form>
  );
}

const timeOf = (iso: string | null) => (iso ? `${String(new Date(iso).getHours()).padStart(2, '0')}:${String(new Date(iso).getMinutes()).padStart(2, '0')}` : '');

/** ✎ on a task or meeting: change its subject, details, date and who it's assigned to, right on the record page. */
function EditForm({ a, onDone, onCancel }: { a: Activity; onDone: () => void; onCancel: () => void }) {
  const users = useUsers();
  const [error, setError] = useState<string | null>(null);
  const voice = splitVoice(a.description);
  const logged = a.kind === 'task' && (a.subtype === 'call' || a.subtype === 'email');
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api(`/api/activities/${a.id}`, { method: 'PATCH', body, headers: { 'if-match': String(a.version) }, quiet: true }),
    onSuccess: () => { toast('Saved'); onDone(); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save.'),
  });
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const f = Object.fromEntries(new FormData(e.currentTarget).entries()) as Record<string, string>;
    // A voice note keeps its recording; only its text changes.
    const text = f.description?.trim() ? f.description : null;
    const body: Record<string, unknown> = { subject: f.subject, description: voice.fileId ? withVoice(text ?? '(voice note without text)', voice.fileId) : text };
    if (!logged && f.ownerId) body.ownerId = f.ownerId;
    if (a.kind === 'task' && !logged) { body.dueDate = f.dueDate || null; body.priority = f.priority; }
    if (a.kind === 'event') {
      const start = new Date(`${f.date}T${f.start}`);
      const end = new Date(`${f.date}T${f.end}`);
      if (end <= start) { setError('The end must be after the start.'); return; }
      Object.assign(body, { startAt: start.toISOString(), endAt: end.toISOString(), location: f.location || null });
    }
    save.mutate(body);
  };
  return (
    <form className="composer act-edit" onSubmit={submit}>
      <div className="composer-grid">
        <div className="field wide"><label htmlFor={`ed-sub-${a.id}`}>Subject</label><input id={`ed-sub-${a.id}`} name="subject" required maxLength={255} autoFocus defaultValue={a.subject} /></div>
        {a.kind === 'task' && !logged && (
          <>
            <div className="field"><label htmlFor={`ed-due-${a.id}`}>Due date</label><input id={`ed-due-${a.id}`} name="dueDate" type="date" defaultValue={a.dueDate ?? ''} /></div>
            <div className="field"><label htmlFor={`ed-pri-${a.id}`}>Priority</label>
              <select id={`ed-pri-${a.id}`} name="priority" defaultValue={a.priority}>{ACTIVITY_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}</select></div>
          </>
        )}
        {a.kind === 'event' && (
          <>
            <div className="field"><label htmlFor={`ed-date-${a.id}`}>Date</label><input id={`ed-date-${a.id}`} name="date" type="date" required defaultValue={a.startAt ? localYmd(new Date(a.startAt)) : today()} /></div>
            <div className="field"><label htmlFor={`ed-start-${a.id}`}>Start</label><input id={`ed-start-${a.id}`} name="start" type="time" required defaultValue={timeOf(a.startAt) || '10:00'} /></div>
            <div className="field"><label htmlFor={`ed-end-${a.id}`}>End</label><input id={`ed-end-${a.id}`} name="end" type="time" required defaultValue={timeOf(a.endAt) || '10:30'} /></div>
            <div className="field"><label htmlFor={`ed-loc-${a.id}`}>Location</label><input id={`ed-loc-${a.id}`} name="location" maxLength={255} defaultValue={a.location ?? ''} /></div>
          </>
        )}
        {!logged && (
          <div className="field"><label htmlFor={`ed-own-${a.id}`}>Assigned to</label>
            <select id={`ed-own-${a.id}`} name="ownerId" defaultValue={a.ownerId}>
              {users.data?.filter((u) => u.isActive || u.id === a.ownerId).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select></div>
        )}
        <div className="field wide"><label htmlFor={`ed-desc-${a.id}`}>{a.kind === 'task' && a.subtype === 'call' ? 'Call notes' : 'Details'}</label>
          <textarea id={`ed-desc-${a.id}`} name="description" rows={Math.min(14, Math.max(3, voice.text.split('\n').length + 1))} maxLength={32000} dir="auto" defaultValue={voice.text} /></div>
      </div>
      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="composer-foot"><button type="button" className="btn pill-btn small" onClick={onCancel}>Cancel</button><button className="btn primary pill-btn small" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button></div>
    </form>
  );
}

function Item({ a, refs, onChange, expanded = true }: { a: Activity; refs: Record<string, string>; onChange: () => void; expanded?: boolean }) {
  const { user, permissions } = useSession();
  const [err, setErr] = useState<string | null>(null);
  const mine = a.ownerId === user.id || permissions.objects.activity.modifyAll;
  // The assignee, whoever wrote it (e.g. the hand-off scope), or Modify All may change it.
  const canEdit = permissions.objects.activity.edit && (mine || (!!a.createdBy && a.createdBy === user.id));
  const [editing, setEditing] = useState(false);
  // Motion: a task just ticked off strikes through and slides away before the list refreshes.
  const [leaving, setLeaving] = useState(false);
  const toggle = useMutation({
    mutationFn: () => api<{ activity?: Activity }>(`/api/activities/${a.id}`, { method: 'PATCH', body: { status: a.status === 'completed' ? 'not_started' : 'completed' }, headers: { 'if-match': String(a.version) } }),
    onSuccess: (res) => {
      if (a.status === 'completed') { onChange(); return; }
      // "Task done" with Undo (back to the status it had: not started or in progress).
      toast('Task done', { undo: () => api(`/api/activities/${a.id}`, { method: 'PATCH', body: { status: a.status }, headers: { 'if-match': String(res?.activity?.version ?? a.version + 1) }, quiet: true }).then(onChange) });
      if (reducedMotion()) { onChange(); return; }
      markTaskDone();
      setLeaving(true);
      window.setTimeout(onChange, 650);
      // Still listed after the refresh (e.g. "All open tasks" shows done ones): show it again.
      window.setTimeout(() => setLeaving(false), 2500);
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not update.'),
  });
  // A deleted activity folds away too.
  const del = useMutation({ mutationFn: (series: 'one' | 'future') => api(`/api/activities/${a.id}?series=${series}`, { method: 'DELETE' }),
    onSuccess: (_r, series) => {
      // "Activity deleted" with Undo (brings it back; only right after deleting, and not for a whole series).
      toast('Activity deleted', series === 'one' ? { undo: () => api(`/api/activities/${a.id}/restore`, { method: 'POST', quiet: true }).then(onChange) } : {});
      if (reducedMotion()) { onChange(); return; }
      setLeaving(true); window.setTimeout(onChange, 650); window.setTimeout(() => setLeaving(false), 2500);
    } });
  // Occurrences of a repeating task or event can take the rest of the series with them.
  const remove = async () => del.mutate(a.seriesId && (await confirmDialog('Also delete the open occurrences after this one?')) ? 'future' : 'one');
  const overdue = isOpenTask(a) && a.dueDate && a.dueDate < today();
  const related = [a.whoId && refs[a.whoId], ...(a.relatedContactIds ?? []).map((c) => refs[c]), a.whatId && refs[a.whatId]].filter(Boolean).join(' · ');
  // Phones: swipe an open task to the right to mark it done (a green ✓ shows under the finger).
  const [dx, setDx] = useState(0);
  const touch = useRef<{ x: number; y: number; on: boolean } | null>(null);
  const canSwipe = a.kind === 'task' && mine && a.status !== 'completed' && !leaving && !editing;
  const swipe = canSwipe ? {
    onTouchStart: (e: TouchEvent<HTMLLIElement>) => { const t = e.touches[0]; touch.current = t ? { x: t.clientX, y: t.clientY, on: false } : null; },
    onTouchMove: (e: TouchEvent<HTMLLIElement>) => {
      const s = touch.current; const t = e.touches[0];
      if (!s || !t) return;
      const mx = t.clientX - s.x, my = t.clientY - s.y;
      if (!s.on && Math.abs(my) > Math.abs(mx)) { touch.current = null; setDx(0); return; }
      if (mx > 10) { s.on = true; setDx(Math.min(140, mx)); }
    },
    onTouchEnd: () => { const go = dx > 90; touch.current = null; setDx(0); if (go && !toggle.isPending) toggle.mutate(); },
    onTouchCancel: () => { touch.current = null; setDx(0); },
  } : {};

  return (
    <li className={`act ${a.kind} ${a.status === 'completed' || leaving || (a.kind === 'event' && !isUpcomingEvent(a)) ? 'done' : ''}${leaving ? ' leaving' : ''}${dx ? ' swiping' : ''}${dx > 90 ? ' swipe-ok' : ''}`}
      style={dx ? { transform: `translateX(${dx}px)` } : undefined} {...swipe}>
      <ActIcon kind={kindOf(a)} size="s" />
      <div className="act-body">
        <div className="act-line">
          {a.kind === 'task' && (
            <input type="checkbox" aria-label={a.status === 'completed' ? 'Mark not done' : 'Mark done'} checked={a.status === 'completed' || leaving} disabled={!mine || toggle.isPending || leaving} onChange={() => toggle.mutate()} />
          )}
          {a.kind === 'task' && a.priority === 'high' && a.status !== 'completed' && a.status !== 'deferred' && <span className="cal-high" title="High priority">HIGH</span>}
          <b>{a.subject}</b>{a.seriesId && <span className="muted small" title="Part of a repeating series"> <Icon name="refresh" /></span>}
        </div>
        <div className="muted small">
          {a.kind === 'event' ? `${formatDateTime(a.startAt)}${a.location ? ` · ${a.location}` : ''}` : a.dueDate ? <span className={overdue ? 'overdue' : ''}>{overdue ? 'Overdue · ' : ''}Due {formatDate(a.dueDate)}</span> : 'No due date'}
          {' · '}{refs[a.ownerId] ?? 'Someone'}{related ? ` · ${related}` : ''}
        </div>
        {/* Colleagues joining the meeting, with their answer. */}
        {(a.guests?.length ?? 0) > 0 && (
          <div className="muted small">With: {a.guests!.map((g) => ` ${refs[g.id] ?? 'a colleague'}`).join(' · ')}</div>
        )}
        {a.description && expanded && !editing && (() => {
          const v = splitVoice(a.description);
          return <>{v.text && <p className="act-desc" dir="auto">{v.text}</p>}{v.fileId && <VoicePlayer fileId={v.fileId} />}</>;
        })()}
        {err && <p className="form-error">{err}</p>}
        {editing && <EditForm a={a} onDone={() => { setEditing(false); onChange(); }} onCancel={() => setEditing(false)} />}
      </div>
      {/* Edit and delete side by side in the row's last column (two loose buttons wrapped the × onto a line of its own). */}
      {((canEdit && !editing) || mine) && (
        <span className="act-actions">
          {canEdit && !editing && <button className="icon small" aria-label={`Edit ${a.subject}`} title="Edit" onClick={() => setEditing(true)}><Icon name="edit" /></button>}
          {mine && <button className="icon small" aria-label={`Delete ${a.subject}`} title="Delete" onClick={remove}>×</button>}
        </span>
      )}
    </li>
  );
}

type TimeKey = 'all' | 'next7' | 'last7' | 'last30';
const dayOffset = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return localYmd(d); };
const TIME_FILTERS: { key: TimeKey; label: string; test: (date: string) => boolean }[] = [
  { key: 'all', label: 'All time', test: () => true },
  { key: 'next7', label: 'Next 7 days', test: (d) => !!d && d.slice(0, 10) >= today() && d.slice(0, 10) <= dayOffset(7) },
  { key: 'last7', label: 'Last 7 days', test: (d) => !!d && d.slice(0, 10) >= dayOffset(-7) && d.slice(0, 10) <= today() },
  { key: 'last30', label: 'Last 30 days', test: (d) => !!d && d.slice(0, 10) >= dayOffset(-30) && d.slice(0, 10) <= today() },
];
const DEFAULT_FILTERS: { time: TimeKey; who: 'all' | 'mine'; type: Mode | 'all' } = { time: 'all', who: 'all', type: 'all' };

/** Activity composer and timeline for a record page. */
export function ActivityPanel({ objectName, recordId, accountId, nextAction }: {
  objectName: ObjectName; recordId: string; accountId?: string | null;
  /** A deal's "Next action" field (and its date): shown first under Upcoming & Overdue. */
  nextAction?: { text: string; date: string | null } | null;
}) {
  const { permissions, user } = useSession();
  const [composing, setComposing] = useState<{ mode: Mode; repeat?: boolean } | null>(null);
  const [menu, setMenu] = useState<Mode | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [expandAll, setExpandAll] = useState(true);
  const [viewAll, setViewAll] = useState(false);
  const [makingTask, setMakingTask] = useState(false);
  const [askNext, setAskNext] = useState(false);
  const qc = useQueryClient();
  const key = ['activities', objectName, recordId];
  const list = useQuery({
    queryKey: key,
    enabled: permissions.objects.activity.read,
    queryFn: () => api<ActivityList>(`/api/activities${qs({ objectName, recordId, limit: 100 })}`),
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ['my-work'] }); };
  if (!permissions.objects.activity.read) return null;

  const time = TIME_FILTERS.find((t) => t.key === filters.time)!;
  const all = (list.data?.activities ?? []).filter((a) =>
    (filters.who === 'all' || a.ownerId === user.id)
    && (filters.type === 'all' || kindOf(a) === filters.type)
    && time.test(String(a.dueDate ?? a.startAt ?? a.createdAt ?? '')),
  );
  // High priority tasks first, then by date.
  const upcoming = all.filter((a) => isOpenTask(a) || isUpcomingEvent(a))
    .sort((x, y) => Number(y.priority === 'high') - Number(x.priority === 'high') || String(x.dueDate ?? x.startAt ?? '9').localeCompare(String(y.dueDate ?? y.startAt ?? '9')));
  const past = all.filter((a) => !upcoming.includes(a));
  const shownPast = viewAll ? past : past.slice(0, 5);
  const refs = list.data?.refs ?? {};
  // The deal's Next action, only while it isn't a task yet: an open task is the next action itself, and a task with
  // the same subject (open or done) already stands for it.
  const nextText = nextAction?.text.trim() ?? '';
  const showNext = !!nextText && !upcoming.some((a) => a.kind === 'task')
    && !all.some((a) => a.kind === 'task' && (a.subject ?? '').trim().toLowerCase() === nextText.toLowerCase());
  const todayStr = today();
  const nextLate = !!nextAction?.date && nextAction.date < todayStr;
  const makeTask = async () => {
    setMakingTask(true);
    try {
      await api('/api/activities', { method: 'POST', body: { kind: 'task', subtype: 'task', subject: nextText.slice(0, 255), dueDate: nextAction?.date && !nextLate ? nextAction.date : todayStr, ownerId: user.id, ...relationFor(objectName, recordId, accountId) } });
      refresh();
    } catch (e) { toast(e instanceof Error ? e.message : 'Could not create the task.'); }
    setMakingTask(false);
  };
  const summary = [time.label, filters.who === 'all' ? 'All activities' : 'My activities', filters.type === 'all' ? 'All types' : `${MODE_LABEL[filters.type].replace(/^(Log an?|New) /, '')}s`].join(' • ');

  return (
    <section className="panel activity-panel">
      {permissions.objects.activity.create && (
        <div className="act-toolbar" role="toolbar" aria-label="New activity">
          {(['email', 'event', 'call', 'task', 'voice'] as const).map((m) => (
            <span key={m} className={`act-split${composing?.mode === m ? ' on' : ''}`}>
              <button className="act-split-main" title={MODE_LABEL[m]} aria-label={MODE_LABEL[m]} onClick={() => setComposing(composing?.mode === m ? null : { mode: m })}>
                <ActIcon kind={m} />
              </button>
              <button className="act-split-more" aria-label={`More ${MODE_LABEL[m].toLowerCase()} options`} aria-expanded={menu === m} onClick={() => setMenu(menu === m ? null : m)}>
                <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 4.5 6 8.5l4-4" fill="currentColor" /></svg>
              </button>
              {menu === m && (
                <div className="card-menu-pop" role="menu">
                  <button role="menuitem" onClick={() => { setMenu(null); setComposing({ mode: m }); }}>{MODE_LABEL[m]}</button>
                  {(m === 'task' || m === 'event') && (
                    <button role="menuitem" onClick={() => { setMenu(null); setComposing({ mode: m, repeat: true }); }}>{m === 'task' ? 'New repeating task' : 'New repeating event'}</button>
                  )}
                </div>
              )}
            </span>
          ))}
        </div>
      )}
      {composing && (
        <Composer key={`${composing.mode}${composing.repeat ? '-r' : ''}`} mode={composing.mode} repeat={composing.repeat} objectName={objectName} recordId={recordId} accountId={accountId}
          onDone={(contact) => {
            refresh(); setComposing(null);
            // A finished meeting or call on a deal with nothing planned: ask for the next step straight away.
            if (contact && objectName === 'opportunity' && !upcoming.some(isOpenTask)) setAskNext(true);
          }} onCancel={() => setComposing(null)} />
      )}
      {askNext && <NextStepPrompt objectName={objectName} recordId={recordId} accountId={accountId} onClose={() => { setAskNext(false); refresh(); }} />}

      <div className="act-filters">
        <span className="muted small">Filters: {summary}</span>
        <span className="card-menu">
          <button className="btn round" aria-label="Filter activities" aria-expanded={showFilters} onClick={() => setShowFilters(!showFilters)}>
            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M10 6.8a3.2 3.2 0 1 0 0 6.4 3.2 3.2 0 0 0 0-6.4Zm6.6 4.2-1.4-.5a5.6 5.6 0 0 0 0-1l1.4-.5-1-2.6-1.4.5a5.5 5.5 0 0 0-.7-.7l.5-1.4-2.6-1-.5 1.4a5.6 5.6 0 0 0-1 0l-.5-1.4-2.6 1 .5 1.4a5.5 5.5 0 0 0-.7.7l-1.4-.5-1 2.6 1.4.5a5.6 5.6 0 0 0 0 1l-1.4.5 1 2.6 1.4-.5c.2.3.4.5.7.7l-.5 1.4 2.6 1 .5-1.4a5.6 5.6 0 0 0 1 0l.5 1.4 2.6-1-.5-1.4c.3-.2.5-.4.7-.7l1.4.5 1-2.6Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /></svg>
          </button>
          {showFilters && (
            <div className="card-menu-pop act-filter-pop">
              <label className="field"><span className="label">Date range</span>
                <select value={filters.time} onChange={(e) => setFilters({ ...filters, time: e.target.value as TimeKey })}>{TIME_FILTERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select>
              </label>
              <label className="field"><span className="label">Activities to show</span>
                <select value={filters.who} onChange={(e) => setFilters({ ...filters, who: e.target.value as 'all' | 'mine' })}><option value="all">All activities</option><option value="mine">My activities</option></select>
              </label>
              <label className="field"><span className="label">Activity type</span>
                <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value as Mode | 'all' })}>
                  <option value="all">All types</option><option value="email">Emails</option><option value="event">Events</option><option value="call">Calls</option><option value="task">Tasks</option><option value="voice">Voice notes</option>
                </select>
              </label>
              <div className="actions">
                <button className="btn small" onClick={() => setFilters(DEFAULT_FILTERS)}>Restore defaults</button>
                <button className="btn primary small" onClick={() => setShowFilters(false)}>Done</button>
              </div>
            </div>
          )}
        </span>
      </div>
      <p className="act-links">
        <button className="link-btn" onClick={refresh}>Refresh</button> •{' '}
        <button className="link-btn" onClick={() => setExpandAll(!expandAll)}>{expandAll ? 'Collapse All' : 'Expand All'}</button> •{' '}
        <button className="link-btn" onClick={() => setViewAll(!viewAll)} disabled={past.length <= 5 && !viewAll}>{viewAll ? 'View Less' : 'View All'}</button>
      </p>

      <details className="act-section" open>
        <summary>Upcoming &amp; Overdue</summary>
        {showNext && (
          <div className={`act-next${nextLate ? ' late' : ''}`}>
            <span className="act-next-tag">Next action</span>
            <b>{nextText}</b>
            <span className="small muted">{nextAction?.date ? `${nextLate ? 'Overdue · was due' : 'Due'} ${formatDate(nextAction.date)}` : 'No date set'}</span>
            {permissions.objects.activity.create && <button className="btn small" disabled={makingTask} onClick={() => void makeTask()}>{makingTask ? 'Creating…' : 'Make it a task'}</button>}
          </div>
        )}
        {upcoming.length ? <ul className="acts">{upcoming.map((a) => <Item key={a.id} a={a} refs={refs} onChange={refresh} expanded={expandAll} />)}</ul>
          : showNext ? null : <p className="act-empty">No activities to show.<br />Get started by logging an email, scheduling a task, and more.</p>}
      </details>
      <details className="act-section" open>
        <summary>Past activity</summary>
        {shownPast.length ? <ul className="acts">{shownPast.map((a) => <Item key={a.id} a={a} refs={refs} onChange={refresh} expanded={expandAll} />)}</ul>
          : <p className="act-empty">{list.isPending ? 'Loading…' : 'No past activity. Past meetings and tasks marked as done show up here.'}</p>}
        {!viewAll && past.length > 5 && <p className="act-links"><button className="link-btn" onClick={() => setViewAll(true)}>Show all {past.length}</button></p>}
      </details>
    </section>
  );
}

/** Compact list used on Home. */
export function MyWorkList({ items, refs, empty }: { items: Activity[]; refs: Record<string, string>; empty: string }) {
  const qc = useQueryClient();
  if (!items.length) return <p className="muted pad empty-anim">{empty}</p>;
  return (
    <ul className="acts pad-x">
      {items.map((a) => (
        <Item key={a.id} a={a} refs={refs} onChange={() => qc.invalidateQueries({ queryKey: ['my-work'] })} />
      ))}
    </ul>
  );
}

